/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Breadcrumb, Button, Card, Empty, Form, Input, Select, Space, Spin, Typography } from 'antd';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import { PlusOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import MainLayout from '../../components/common/MainLayout';
import {
  useCreateCropAttributeMutation,
  useGetCropAttributesQuery,
  useGetCropTaxonomyTreeQuery,
  useGetTaxonomyAttributesQuery,
  useGetAvailableAttributesQuery,
  useSetTaxonomyAttributeValueMutation,
} from '../../api/cropsApi';
import { AttributeCard } from '../../components/attributes/AttributeCard';
import { TaxonomySelector, buildTaxonomyOptions, getFullTaxonomyPath } from '../../components/attributes/TaxonomySelector';
import { useAttributeEditor } from '../../components/attributes/useAttributeEditor';
import { EditAttributeModal } from '../../components/attributes/EditAttributeModal';
import { CreateAttributeModal } from '../../components/attributes/CreateAttributeModal';
import { EditAttributeValueModal } from '../../components/attributes/EditAttributeValueModal';
import { TaxonomyAttributesList } from '../../components/attributes/TaxonomyAttributesList';
import { useFarmPermissions } from '../../components/attributes/useFarmPermissions';
import { useSave, useWorld } from '../../contexts/WorldContext';
import { useTaxonomyScope } from '../../components/attributes/useTaxonomyScope';
import { useAttributeBinding } from '../../components/attributes/useAttributeBinding';
import { toastError, toastSuccess } from '../../utils/toast';
import { wrapQueryResult } from '../../utils/queryHelpers';
import type { CropAttributeCreatePayload, CropAttributeOptionInput } from '../../models/Crop';

const { Text, Title, Paragraph } = Typography;

const styles = {
  titleNoMargin: css`
    margin: 0;
  `,
  paragraphNoMargin: css`
    margin: 0;
  `,
  smallSecondaryText: css`
    font-size: 12px;
    font-weight: normal;
  `,
  selectMinWidth: css`
    min-width: 200px;
  `,
  inputWidth150: css`
    width: 150px;
  `,
};

const FarmAttributesPage: React.FC = () => {
  const { saveId } = useParams<{ saveId: string }>();
  const { t } = useTranslation('crops');
  const save = useSave();
  const world = useWorld();
  const farmId = save.farmId;
  const saveTitle = save.farmName || save.id.slice(0, 8);
  
  const user = useSelector((state: any) => state.auth.user);
  const userActions = useSelector((state: any) => state.auth.user?.actions || []);
  const farmData = useMemo(() => ({
    farm: {
      id: farmId,
      name: saveTitle,
      is_owner: world.farm?.isOwner ?? false,
    },
    actions: (world.allowedActions?.global || []).map((code) => ({ title: '', code })),
  }), [farmId, saveTitle, world.allowedActions?.global, world.farm?.isOwner]);

  // Права доступа
  const { canManageFarmAttributes } = useFarmPermissions(user, userActions, farmData, farmId);

  // Таксономия
  const { data: taxonomy, isLoading: taxonomyTreeLoading } = useGetCropTaxonomyTreeQuery();
  const taxonomyOptions = useMemo(() => buildTaxonomyOptions(taxonomy), [taxonomy]);
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  
  const selectedScope = useTaxonomyScope(selectedPath);
  const fullTaxonomyPath = useMemo(
    () => getFullTaxonomyPath(taxonomy, selectedPath),
    [taxonomy, selectedPath],
  );

  // Атрибуты сейва (Wave 5.2)
  const {
    data: farmAttributes = [],
    isFetching: farmAttributesLoading,
    error: farmAttributesError,
    refetch: refetchFarmAttributes,
  } = useGetCropAttributesQuery(
    saveId
      ? { scopeType: 'save', saveId }
      : skipToken,
  );

  // Атрибуты таксономии
  const {
    data: taxonomyAttributes = [],
    isFetching: taxonomyAttributesLoading,
    refetch: refetchTaxonomyAttributes,
  } = useGetTaxonomyAttributesQuery(
    selectedScope && saveId
      ? { scope: selectedScope.scope, scopeId: selectedScope.scopeId, saveId }
      : skipToken,
  );

  // Доступные атрибуты для привязки (с фильтрацией по таксономии)
  const {
    data: availableAttributesFromApi = [],
    isFetching: availableAttributesLoading,
  } = useGetAvailableAttributesQuery(
    selectedScope && saveId
      ? { scope: selectedScope.scope, scopeId: selectedScope.scopeId, saveId }
      : skipToken,
  );

  // Убираем уже привязанные атрибуты из списка доступных
  const availableAttributes = useMemo(() => {
    if (!selectedScope) return [];
    const bound = new Set(taxonomyAttributes.map((item) => String(item.attribute.id)));
    return availableAttributesFromApi.filter((attr) => !bound.has(String(attr.id)));
  }, [availableAttributesFromApi, taxonomyAttributes, selectedScope]);

  const filteredAvailableAttributes = useMemo(() => {
    if (!search.trim()) return availableAttributes;
    const normalized = search.trim().toLowerCase();
    return availableAttributes.filter((attribute) => {
      const nameMatch = attribute.name.toLowerCase().includes(normalized);
      const descriptionMatch = (attribute.description ?? '').toLowerCase().includes(normalized);
      return nameMatch || descriptionMatch;
    });
  }, [availableAttributes, search]);

  // Модальные окна и формы
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [createCropAttribute, { isLoading: creatingAttribute }] = useCreateCropAttributeMutation();

  const [editValueModalOpen, setEditValueModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<any>(null);
  const [editValueForm] = Form.useForm();
  const [setTaxonomyAttributeValue, { isLoading: savingValue }] = useSetTaxonomyAttributeValueMutation();

  // Привязка атрибутов
  const {
    attributeToBind,
    setAttributeToBind,
    bindingValue,
    setBindingValue,
    creatingBinding,
    handleAddAttributeToScope,
    // resetBinding, // Not used currently
  } = useAttributeBinding({
    saveId: saveId || '',
    onSuccess: async () => {
      await Promise.all([wrapQueryResult(refetchTaxonomyAttributes()), refetchFarmAttributes()]);
    },
  });

  // Редактор атрибутов
  const {
    editForm,
    editModalOpen,
    isLoadingAttribute,
    handleCloseEditModal,
    handleEditFormValuesChange,
    handleOpenEditAttribute,
    handleUpdateFarmAttribute,
  } = useAttributeEditor(async () => {
    await Promise.all([
      refetchFarmAttributes(),
      selectedScope ? wrapQueryResult(refetchTaxonomyAttributes()) : Promise.resolve(),
    ]);
  }, saveId);

  const selectedAttributeForBinding = useMemo(() => {
    if (!attributeToBind) return null;
    return availableAttributesFromApi.find((attr) => String(attr.id) === String(attributeToBind));
  }, [attributeToBind, availableAttributesFromApi]);

  const handleOpenEditValue = (item: any) => {
    setEditingAttribute(item);
    const binding = item.binding;
    
    if (binding?.choiceId != null) {
      editValueForm.setFieldsValue({ value: { choiceId: String(binding.choiceId) } });
    } else if (binding?.customValue) {
      editValueForm.setFieldsValue({ value: { customValue: binding.customValue } });
    } else {
      editValueForm.setFieldsValue({ value: {} });
    }
    
    setEditValueModalOpen(true);
  };

  const handleSaveAttributeValue = async () => {
    if (!selectedScope || !editingAttribute) return;
    
    try {
      const values = await editValueForm.validateFields();
      const hasOptions = Array.isArray(editingAttribute.attribute.options) && editingAttribute.attribute.options.length > 0;
      
      if (!saveId) {
        toastError(t('details.errors.noSave'));
        return;
      }
      const payload: any = {
        scope: selectedScope.scope,
        scopeId: selectedScope.scopeId,
        attributeId: editingAttribute.attribute.id,
        saveId,
      };
      
      if (hasOptions) {
        const choiceId = values.value?.choiceId;
        payload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
        payload.customValue = null;
      } else {
        payload.choiceId = null;
        payload.customValue = values.value?.customValue?.trim() || null;
      }
      
      await setTaxonomyAttributeValue(payload).unwrap();
      toastSuccess(t('details.toasts.attributeValueUpdated'));
      setEditValueModalOpen(false);
      setEditingAttribute(null);
      editValueForm.resetFields();
      await wrapQueryResult(refetchTaxonomyAttributes());
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleCreateAttribute = async () => {
    try {
      const values = await createForm.validateFields();
      
      const options: CropAttributeOptionInput[] = (values.options || [])
        .map((item: any, index: number) => {
          const title = item?.title?.trim();
          if (!title) return null;
          const code = (item?.code ?? '').trim() || title;
          const option: CropAttributeOptionInput = { title, code, sortOrder: index };
          if (item?.id != null) {
            option.id = item.id;
          }
          return option;
        })
        .filter((option): option is CropAttributeOptionInput => option !== null);

      if (!saveId) {
        toastError(t('details.errors.noSave'));
        return;
      }
      const payload: CropAttributeCreatePayload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        scopeType: 'save',
        options: options.length ? options : null,
        binding: null,
      };

      await createCropAttribute({ ...payload, saveId }).unwrap();
      toastSuccess(t('attributesPage.toasts.created'));
      setCreateModalOpen(false);
      createForm.resetFields();
      await refetchFarmAttributes();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleBindClick = async () => {
    if (!selectedScope || !selectedAttributeForBinding) return;
    await handleAddAttributeToScope(
      selectedScope.scope,
      selectedScope.scopeId,
      selectedAttributeForBinding,
    );
  };

  // После загрузки данных фермы проверяем права
  if (!canManageFarmAttributes) {
    return (
      <MainLayout
        breadcrumb={
          <Breadcrumb
            items={[
              { title: <Link to={`/saves/${saveId}`}>{saveTitle}</Link> },
              { title: t('attributesPage.breadcrumb') },
            ]}
          />
        }
      >
        <Card>
          <Card.Meta
            title={t('attributesPage.noAccess.title')}
            description={t('attributesPage.noAccess.description')}
          />
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to={`/saves/${saveId}`}>{saveTitle}</Link> },
            { title: t('attributesPage.breadcrumb') },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
            <Title level={4} css={styles.titleNoMargin}>
              {t('attributesPage.title')}
            </Title>
            <Paragraph type="secondary" css={styles.paragraphNoMargin}>
              {t('attributesPage.help')}
            </Paragraph>
          </Space>
        </Card>

        <Card 
          title={t('attributesPage.title')}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                createForm.resetFields();
                setCreateModalOpen(true);
              }}
            >
              {t('attributesPage.create')}
            </Button>
          }
        >
          {farmAttributesLoading ? (
            <Spin />
          ) : farmAttributesError ? (
            <Card type="inner" size="small">
              <Text type="danger">{t('attributesPage.loadFailed')}</Text>
            </Card>
          ) : farmAttributes.length === 0 ? (
            <Empty description={t('attributesPage.empty')} />
          ) : (
            <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
              {farmAttributes.map((attribute) => (
                <AttributeCard
                  key={String(attribute.id)}
                  attribute={attribute}
                  onEdit={handleOpenEditAttribute}
                />
              ))}
            </Space>
          )}
        </Card>

        <TaxonomySelector
          taxonomyOptions={taxonomyOptions}
          selectedPath={selectedPath}
          onPathChange={setSelectedPath}
          search={search}
          onSearchChange={setSearch}
          loading={taxonomyTreeLoading}
          disabled={false}
        />

        {selectedScope && (
          <Card
            title={
              <Space direction="vertical" size={0}>
                <Text>{selectedScope.label ?? '—'}</Text>
                {fullTaxonomyPath && (
                  <Text type="secondary" css={styles.smallSecondaryText}>
                    {fullTaxonomyPath}
                  </Text>
                )}
              </Space>
            }
            extra={
              <Space>
                <Select
                  placeholder={t('details.attributes.bind')}
                  value={attributeToBind}
                  onChange={(value) => {
                    setAttributeToBind(value);
                    setBindingValue({});
                  }}
                  css={styles.selectMinWidth}
                  size="small"
                  showSearch
                  optionFilterProp="label"
                  options={filteredAvailableAttributes.map((attribute) => ({
                    value: String(attribute.id),
                    label: attribute.description
                      ? `${attribute.name} — ${attribute.description}`
                      : attribute.name,
                  }))}
                  disabled={filteredAvailableAttributes.length === 0 || creatingBinding}
                  loading={availableAttributesLoading}
                />
                {attributeToBind && selectedAttributeForBinding && (
                  selectedAttributeForBinding.options && selectedAttributeForBinding.options.length > 0 ? (
                    <Select
                      placeholder={t('details.valueOptional')}
                      value={bindingValue.choiceId}
                      onChange={(value) => setBindingValue({ choiceId: value })}
                      css={{ minWidth: 150 }}
                      size="small"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={selectedAttributeForBinding.options.map((option: any) => ({
                        value: String(option.id),
                        label: option.code && option.code !== option.title
                          ? `${option.title} (${option.code})`
                          : option.title,
                      }))}
                      disabled={creatingBinding}
                    />
                  ) : (
                    <Input
                      placeholder={t('details.valueOptional')}
                      value={bindingValue.customValue ?? ''}
                      onChange={(e) => setBindingValue({ customValue: e.target.value })}
                      css={styles.inputWidth150}
                      size="small"
                      allowClear
                      disabled={creatingBinding}
                    />
                  )
                )}
                <Button
                  size="small"
                  onClick={handleBindClick}
                  disabled={!attributeToBind || creatingBinding}
                  loading={creatingBinding}
                >
                  {t('details.bindAction')}
                </Button>
              </Space>
            }
          >
            {taxonomyAttributesLoading ? (
              <Spin />
            ) : (
              <TaxonomyAttributesList
                attributes={taxonomyAttributes}
                loading={taxonomyAttributesLoading}
                onEdit={handleOpenEditValue}
              />
            )}
          </Card>
        )}
      </Space>

      <EditAttributeValueModal
        open={editValueModalOpen}
        onOk={handleSaveAttributeValue}
        onCancel={() => {
          setEditValueModalOpen(false);
          setEditingAttribute(null);
          editValueForm.resetFields();
        }}
        form={editValueForm}
        loading={savingValue}
        attribute={editingAttribute?.attribute}
      />

      <CreateAttributeModal
        open={createModalOpen}
        onOk={handleCreateAttribute}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        form={createForm}
        loading={creatingAttribute}
      />

      <EditAttributeModal
        open={editModalOpen}
        onCancel={handleCloseEditModal}
        onOk={handleUpdateFarmAttribute}
        form={editForm}
        onValuesChange={handleEditFormValuesChange}
        loading={isLoadingAttribute}
      />
    </MainLayout>
  );
};

export default FarmAttributesPage;